// Whole-app translation. Watches the page and swaps the app's English text (and placeholders, tooltips,
// screen-reader labels) for the person's language. Translations come from a shared library (ui_translations),
// filled by AI the first time a phrase is seen in that language, then reused by everyone.
// Not translated: anything inside [data-no-translate] (chat messages, docs, people's own writing), inputs,
// editors, code, and text that is only numbers / dates / amounts.
import { supabase } from '@/src/lib/supabase';

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA', 'NOSCRIPT', 'svg', 'SVG', 'KBD']);
const SKIP_SEL = '[data-no-translate],[contenteditable="true"],.ProseMirror,.opsalo-doc';
const ATTRS = ['placeholder', 'title', 'aria-label'] as const;
const LETTER = /\p{L}/u;

/** Worth translating: has real words, isn't just a number / date / time / amount / email / link. */
function worth(t: string): boolean {
  if (t.length < 2 || t.length > 2000 || !LETTER.test(t)) return false;
  if (/^[\w.+-]+@[\w-]+\.[\w.]+$/.test(t) || /^https?:\/\//.test(t)) return false;
  if (/\d/.test(t)) {
    const words = t.replace(/[\d$€£%.,:;/()+\-–—#@×*]/g, ' ').split(/\s+/).filter((w) => LETTER.test(w));
    if (words.length <= 2 && words.every((w) => w.length <= 4)) return false; // "Mon 26 Oct", "5:00pm", "12 kg"
  }
  return true;
}

type Rec = { src: string; out: string | null };

export class TranslateLayer {
  private dict = new Map<string, string>();
  private nodes = new WeakMap<Node, Rec>();
  private attrs = new WeakMap<Element, Partial<Record<(typeof ATTRS)[number], Rec>>>();
  private queue = new Set<string>();
  private asked = new Set<string>();
  private obs: MutationObserver | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private raf = 0;
  private dirty = new Set<Node>();
  private stopped = false;

  constructor(private lang: string) {}

  async start() {
    try { const raw = localStorage.getItem(`opsalo:ui-tr:${this.lang}`); if (raw) for (const [k, v] of Object.entries(JSON.parse(raw) as Record<string, string>)) this.dict.set(k, v); } catch { /* ignore */ }
    document.documentElement.lang = this.lang;
    this.scan(document.body);
    this.obs = new MutationObserver((ms) => {
      for (const m of ms) {
        if (m.type === 'characterData') this.dirty.add(m.target);
        else if (m.type === 'attributes') this.dirty.add(m.target);
        else m.addedNodes.forEach((n) => this.dirty.add(n));
      }
      if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = 0; const d = [...this.dirty]; this.dirty.clear(); d.forEach((n) => n.isConnected && this.scan(n)); });
    });
    this.obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...ATTRS] });
    // Everything already translated for this language, in one go (so most screens show translated straight away).
    for (let from = 0; from < 20000; from += 1000) {
      const { data, error } = await supabase.from('ui_translations').select('src, txt').eq('lang', this.lang).range(from, from + 999);
      if (error || !data?.length || this.stopped) break;
      for (const r of data as Array<{ src: string; txt: string }>) this.dict.set(r.src, r.txt);
      if (data.length < 1000) break;
    }
    if (!this.stopped) this.scan(document.body);
  }

  stop() { this.stopped = true; this.obs?.disconnect(); if (this.timer) clearTimeout(this.timer); if (this.raf) cancelAnimationFrame(this.raf); }

  private skip(el: Element | null): boolean {
    for (let e = el; e; e = e.parentElement) if (SKIP_TAGS.has(e.tagName)) return true;
    return !!el?.closest(SKIP_SEL);
  }

  private scan(root: Node) {
    if (root.nodeType === Node.TEXT_NODE) { this.text(root as Text); return; }
    if (root.nodeType !== Node.ELEMENT_NODE) return;
    const el = root as Element;
    if (this.skip(el)) return;
    this.attrsOf(el);
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, {
      acceptNode: (n) => {
        if (n.nodeType === Node.ELEMENT_NODE) {
          const e = n as Element;
          if (SKIP_TAGS.has(e.tagName) || e.matches(SKIP_SEL)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    // Attributes on descendants (placeholder, title, aria-label).
    el.querySelectorAll('[placeholder],[title],[aria-label]').forEach((e) => { if (!e.closest(SKIP_SEL)) this.attrsOf(e); });
    for (let n = walker.nextNode(); n; n = walker.nextNode()) this.text(n as Text);
  }

  private text(node: Text) {
    const cur = node.nodeValue ?? '';
    const rec = this.nodes.get(node);
    if (rec && rec.out !== null && cur === rec.out) return; // our own translation
    if (!node.parentElement || this.skip(node.parentElement)) return;
    const t = cur.trim();
    if (!worth(t)) { this.nodes.delete(node); return; }
    const r: Rec = { src: cur, out: null };
    this.nodes.set(node, r);
    const tr = this.dict.get(t);
    if (tr != null) {
      const out = cur.replace(t, tr);
      r.out = out;
      if (out !== cur) node.nodeValue = out;
    } else this.ask(t);
  }

  private attrsOf(el: Element) {
    const recs = this.attrs.get(el) ?? {};
    for (const a of ATTRS) {
      const cur = el.getAttribute(a);
      if (cur == null) continue;
      const rec = recs[a];
      if (rec && rec.out !== null && cur === rec.out) continue;
      const t = cur.trim();
      if (!worth(t)) { delete recs[a]; continue; }
      const r: Rec = { src: cur, out: null };
      recs[a] = r;
      const tr = this.dict.get(t);
      if (tr != null) { r.out = tr; if (tr !== cur) el.setAttribute(a, tr); } else this.ask(t);
    }
    this.attrs.set(el, recs);
  }

  private ask(t: string) {
    if (this.asked.has(t)) return;
    this.queue.add(t);
    if (!this.timer) this.timer = setTimeout(() => void this.flush(), 350);
  }

  private async flush() {
    this.timer = null;
    const batch = [...this.queue].slice(0, 80);
    batch.forEach((t) => { this.queue.delete(t); this.asked.add(t); });
    if (!batch.length || this.stopped) return;
    const { data } = await supabase.functions.invoke('ui-translate', { body: { lang: this.lang, texts: batch } });
    const got = ((data as { translations?: Record<string, string> } | null)?.translations) ?? {};
    let any = false;
    for (const [k, v] of Object.entries(got)) { this.dict.set(k, v); any = true; }
    if (any) {
      try {
        const keep = Object.fromEntries([...this.dict.entries()].slice(-6000));
        localStorage.setItem(`opsalo:ui-tr:${this.lang}`, JSON.stringify(keep));
      } catch { /* storage full: fine */ }
      if (!this.stopped) this.scan(document.body);
    }
    if (this.queue.size && !this.timer) this.timer = setTimeout(() => void this.flush(), 150);
  }
}

/** One-off translation of a piece of content (e.g. a team chat message). Not saved to the shared library. */
export async function translateOnce(text: string, lang: string): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke('ui-translate', { body: { lang, texts: [text], store: false } });
  if (error) return null;
  return ((data as { translations?: Record<string, string> } | null)?.translations ?? {})[text] ?? null;
}
